/**
 * Custom Apparel — Bulk Size Grid + Service Add-on + File Uploads
 *
 * Web component <custom-apparel-form> orchestrates:
 *  - Color swatch selection → size grid row update
 *  - Size qty inputs → running total
 *  - Service add-on dropdown → variant selection + upload zone visibility
 *  - File upload with preview
 *  - Bulk add-to-cart via Shopify Cart API (/cart/add.js)
 */

class CustomApparelForm extends HTMLElement {
  constructor() {
    super();
    this.uploadedFiles = {};
    this.pendingUploads = new Map();
    this.colorSlots = []; // saved color selections: [{ color, swatchStyle, variants: { vid: qty } }]
  }

  connectedCallback() {
    this.cacheElements();
    this.bindColorSwatches();
    this.bindSizeQtyInputs();
    this.bindAddonSelect();
    this.bindFileUploads();
    this.bindAddToCart();
    this.updateTotal();
    this.initMobileSizeGrid();
    this.initMultiColor();
    this.restoreEditState();
  }

  /* ── Cache DOM References ── */
  cacheElements() {
    this.colorSwatches = this.querySelectorAll('[data-ca-color]');
    this.sizeQtyInputs = this.querySelectorAll('[data-ca-qty]');
    this.selectedColorEl = this.querySelector('[data-ca-selected-color]');
    this.colorChipEl = this.querySelector('[data-ca-color-chip]');
    this.colorNameEl = this.querySelector('[data-ca-color-name]');
    this.addonCheckboxes = this.querySelectorAll('[data-ca-addon-check]');
    this.addonOptionsEl = this.querySelector('[data-ca-addon-options]');
    this.addonPriceEl = this.querySelector('[data-ca-addon-price]');
    this.uploadZones = this.querySelectorAll('[data-ca-upload-zone]');
    this.atcBtn = this.querySelector('[data-ca-atc]');
    this.errorEl = this.querySelector('[data-ca-error]');
    this.successEl = this.querySelector('[data-ca-success]');
    this.totalEl = this.querySelector('[data-ca-total]');
    this.totalQtyEl = this.querySelector('[data-ca-total-qty]');

    this.productData = JSON.parse(this.getAttribute('data-product-json') || '{}');

    // Discount config from sibling Customization Discount block
    this.discountEl = this.querySelector('[data-ca-discount-enabled]');
    this.discountEnabled = !!this.discountEl;
    this.discountType = this.discountEl?.getAttribute('data-ca-discount-type') || 'percentage';
    this.discountValue = parseInt(this.discountEl?.getAttribute('data-ca-discount-value') || '0', 10);
    if (this.discountEnabled && this.discountValue > 0) this.applyDiscountDisplay();

    // Minimum threshold config
    this.orderBlock = this.querySelector('[data-ca-min-enabled]');
    this.minEnabled = this.orderBlock?.getAttribute('data-ca-min-enabled') === 'true';
    this.minQty = parseInt(this.orderBlock?.getAttribute('data-ca-min-qty') || '0', 10);
    this.setupFeeId = this.orderBlock?.getAttribute('data-ca-setup-fee-id') || '';
    this.setupFeePrice = parseInt(this.orderBlock?.getAttribute('data-ca-setup-fee-price') || '0', 10);
    this.setupFeeTitle = this.orderBlock?.getAttribute('data-ca-setup-fee-title') || '';
    this.minMsg = this.orderBlock?.getAttribute('data-ca-min-msg') || '';
    this.minMetMsg = this.orderBlock?.getAttribute('data-ca-min-met-msg') || '';
    this.minMsgEl = this.querySelector('[data-ca-min-msg-el]');
  }

  /* ── Color Swatch Selection ── */
  bindColorSwatches() {
    this.colorSwatches.forEach(swatch => {
      swatch.addEventListener('click', () => {
        const color = swatch.getAttribute('data-ca-color');
        this.selectColor(color, swatch);
      });
    });
  }

  selectColor(color, swatch) {
    // Update active state
    this.colorSwatches.forEach(s => s.classList.remove('is-active'));
    swatch.classList.add('is-active');

    // Update selected color display
    if (this.selectedColorEl) this.selectedColorEl.textContent = color;
    if (this.colorChipEl) {
      this.colorChipEl.style.backgroundColor = swatch.style.backgroundColor;
      this.colorChipEl.style.backgroundImage = swatch.style.backgroundImage;
    }
    if (this.colorNameEl) this.colorNameEl.textContent = color;

    // Show/hide size rows for this color
    this.querySelectorAll('[data-ca-size-row]').forEach(row => {
      const rowColor = row.getAttribute('data-ca-size-row');
      const inputs = row.querySelectorAll('[data-ca-qty]');
      const hidden = row.querySelectorAll('[data-ca-variant-id]');
      if (rowColor === color) {
        row.style.display = '';
        inputs.forEach(i => i.removeAttribute('disabled'));
        hidden.forEach(h => h.removeAttribute('disabled'));
      } else {
        row.style.display = 'none';
        inputs.forEach(i => { i.setAttribute('disabled', ''); i.value = '0'; });
        hidden.forEach(h => h.setAttribute('disabled', ''));
      }
    });

    this.updateTotal();

    // Sync with theme's media gallery for image swaps
    this.syncProductImage(color, swatch);
  }

  /* ── Image Sync ── */
  syncProductImage(color, swatch) {
    // Same approach as the reference theme: click the real hidden variant picker's radio
    // The theme's variant-picker JS handles the media gallery update natively
    const variantPicker = document.querySelector('variant-picker');
    if (variantPicker) {
      const radios = variantPicker.querySelectorAll('input[type="radio"]');
      for (const radio of radios) {
        const val = (radio.value || '').trim();
        if (val === color || val.toLowerCase() === color.toLowerCase()) {
          // Temporarily allow pointer events so the click registers
          variantPicker.style.pointerEvents = 'auto';
          radio.click();
          // Re-hide after a tick
          setTimeout(() => { variantPicker.style.pointerEvents = ''; }, 50);
          return;
        }
      }
      // Try select dropdowns
      const selects = variantPicker.querySelectorAll('select');
      for (const sel of selects) {
        for (const opt of sel.options) {
          if ((opt.value || '').trim() === color || (opt.value || '').trim().toLowerCase() === color.toLowerCase()) {
            sel.value = opt.value;
            sel.dispatchEvent(new Event('change', { bubbles: true }));
            return;
          }
        }
      }
    }
  }

  /* ── Size Qty Inputs ── */
  bindSizeQtyInputs() {
    this.querySelectorAll('[data-ca-qty]').forEach(input => {
      input.addEventListener('input', () => this.updateTotal());
      input.addEventListener('change', () => this.updateTotal());
    });
  }

  updateTotal() {
    let totalQty = 0;
    let totalPrice = 0;

    // Count active grid
    this.querySelectorAll('[data-ca-qty]:not([disabled])').forEach(input => {
      const qty = parseInt(input.value, 10) || 0;
      const price = parseInt(input.getAttribute('data-variant-price') || '0', 10);
      totalQty += qty;
      totalPrice += qty * price;
    });

    // Count saved slots
    this.colorSlots.forEach(slot => {
      Object.entries(slot.variants).forEach(([vid, qty]) => {
        totalQty += qty;
        // Find variant price from DOM
        const hidden = this.querySelector(`[data-ca-variant-id][value="${vid}"]`);
        const qtyInput = hidden?.closest('td')?.querySelector('[data-ca-qty]');
        const price = parseInt(qtyInput?.getAttribute('data-variant-price') || '0', 10);
        totalPrice += qty * price;
      });
    });

    // Add addon prices for all checked locations
    const checkedAddons = this.getCheckedAddons ? this.getCheckedAddons() : [];
    checkedAddons.forEach(cb => {
      const addonPrice = parseInt(cb.getAttribute('data-price') || '0', 10);
      totalPrice += totalQty * addonPrice;
    });

    // Add setup fee if below minimum
    let setupFeeApplies = false;
    if (this.minEnabled && totalQty > 0 && totalQty < this.minQty && this.setupFeeId) {
      totalPrice += this.setupFeePrice;
      setupFeeApplies = true;
    }

    if (this.totalEl) {
      let label = `Total: <span>${this.formatMoney(totalPrice)}</span> (${totalQty} items)`;
      if (setupFeeApplies) label += ` <small style="opacity:0.6;">+ setup fee</small>`;
      this.totalEl.innerHTML = label;
    }
    if (this.totalQtyEl) this.totalQtyEl.textContent = totalQty;

    // Update minimum threshold message
    this.updateMinimumStatus(totalQty);
  }

  updateMinimumStatus(totalQty) {
    if (!this.minEnabled || !this.minMsgEl) return;

    const min = this.minQty;
    if (totalQty <= 0) {
      this.minMsgEl.textContent = '';
      this.minMsgEl.closest('.ca-minimum')?.classList.remove('is-below', 'is-met');
      return;
    }

    const container = this.minMsgEl.closest('.ca-minimum');
    if (totalQty < min) {
      this.minMsgEl.textContent = this.minMsg.replace('MINIMUM', min).replace('{{min}}', min);
      container?.classList.add('is-below');
      container?.classList.remove('is-met');
    } else {
      this.minMsgEl.textContent = this.minMetMsg.replace('MINIMUM', min).replace('{{min}}', min);
      container?.classList.remove('is-below');
      container?.classList.add('is-met');
    }
  }

  formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  /* ── Discount Display ── */
  applyDiscountDisplay() {
    this.addonCheckboxes.forEach(cb => {
      const originalPrice = parseInt(cb.getAttribute('data-original-price') || '0', 10);
      let discounted = originalPrice;

      if (this.discountType === 'percentage') {
        discounted = Math.round(originalPrice * (100 - this.discountValue) / 100);
      } else {
        discounted = Math.max(0, originalPrice - (this.discountValue * 100));
      }

      cb.setAttribute('data-price', discounted);

      const priceEl = cb.closest('.ca-addon__check')?.querySelector('[data-ca-price-display]');
      if (priceEl && discounted !== originalPrice) {
        priceEl.innerHTML = `<s class="ca-addon__original-price">${this.formatMoney(originalPrice)}</s> <span class="ca-addon__sale-price">${this.formatMoney(discounted)}</span>`;
      }
    });
  }

  /* ── Addon Multi-Select ── */
  bindAddonSelect() {
    if (!this.addonCheckboxes.length) return;

    this.addonCheckboxes.forEach(cb => {
      cb.addEventListener('change', () => {
        this.updateUploadZones();
        this.updateAddonPrice();
        this.updateTotal();
      });
    });
  }

  getCheckedAddons() {
    return Array.from(this.addonCheckboxes).filter(cb => cb.checked);
  }

  updateAddonPrice() {
    const checked = this.getCheckedAddons();
    if (!this.addonPriceEl) return;

    if (checked.length === 0) {
      this.addonPriceEl.innerHTML = '';
      return;
    }

    const totalAddonPrice = checked.reduce((sum, cb) => sum + (parseInt(cb.getAttribute('data-price') || '0', 10)), 0);
    this.addonPriceEl.innerHTML = `Adds <span>${this.formatMoney(totalAddonPrice)}</span> per item for ${checked.length} location${checked.length > 1 ? 's' : ''}`;
  }

  updateUploadZones() {
    // Hide all zones first
    this.uploadZones.forEach(zone => zone.classList.remove('is-visible'));

    // Toggle hint text
    const hint = this.querySelector('[data-ca-upload-hint]');

    const checked = this.getCheckedAddons();
    if (checked.length === 0) {
      if (hint) hint.removeAttribute('hidden');
      return;
    }
    if (hint) hint.setAttribute('hidden', '');

    checked.forEach(cb => {
      const value = (cb.getAttribute('data-value') || '').toLowerCase().trim();

      // Try exact match first, then partial match
      let matched = false;
      this.uploadZones.forEach(zone => {
        const zoneName = (zone.getAttribute('data-ca-upload-zone') || '').toLowerCase().trim();
        if (zoneName === 'generic') return;
        // Exact match or the variant name equals the zone name
        if (zoneName === value || value === zoneName) {
          zone.classList.add('is-visible');
          matched = true;
        }
      });

      // If no specific zone matched, show the generic one
      if (!matched) {
        const generic = this.querySelector('[data-ca-upload-zone="generic"]');
        if (generic) generic.classList.add('is-visible');
      }
    });
  }

  /* ── File Uploads ── */
  bindFileUploads() {
    this.querySelectorAll('[data-ca-file-input]').forEach(input => {
      const zone = input.closest('[data-ca-upload-zone]');
      const dropzone = zone?.querySelector('.ca-uploads__dropzone');
      const preview = zone?.querySelector('.ca-uploads__preview');
      const thumb = zone?.querySelector('.ca-uploads__preview-thumb');
      const nameEl = zone?.querySelector('.ca-uploads__preview-name');
      const removeBtn = zone?.querySelector('.ca-uploads__preview-remove');
      const key = input.getAttribute('data-ca-file-key');

      // File selection
      input.addEventListener('change', () => {
        const file = input.files[0];
        if (!file) return;
        this.handleFile(file, key, dropzone, preview, thumb, nameEl);
      });

      // Drag & drop
      if (dropzone) {
        dropzone.addEventListener('dragover', e => { e.preventDefault(); dropzone.classList.add('is-dragover'); });
        dropzone.addEventListener('dragleave', () => dropzone.classList.remove('is-dragover'));
        dropzone.addEventListener('drop', e => {
          e.preventDefault();
          dropzone.classList.remove('is-dragover');
          const file = e.dataTransfer.files[0];
          if (file) {
            // Set file on input for form compatibility
            const dt = new DataTransfer();
            dt.items.add(file);
            input.files = dt.files;
            this.handleFile(file, key, dropzone, preview, thumb, nameEl);
          }
        });
      }

      // Remove file
      if (removeBtn) {
        removeBtn.addEventListener('click', () => {
          input.value = '';
          delete this.uploadedFiles[key];
          if (preview) preview.classList.remove('has-file');
          if (dropzone) dropzone.style.display = '';
        });
      }
    });
  }

  async handleFile(file, key, dropzone, preview, thumb, nameEl) {
    if (nameEl) nameEl.textContent = `Uploading ${file.name}...`;
    if (preview) preview.classList.add('has-file');
    if (dropzone) dropzone.style.display = 'none';

    // Preview image locally
    if (thumb && file.type.startsWith('image/')) {
      thumb.src = URL.createObjectURL(file);
      thumb.style.display = '';
    } else if (thumb) {
      thumb.style.display = 'none';
    }

    // Upload to Dropbox via Railway API
    const uploadPromise = this.uploadToShopify(file).then(url => {
      console.log('Upload success:', key, url);
      this.uploadedFiles[key] = url;
      if (nameEl) nameEl.textContent = `✓ ${file.name}`;
      this.pendingUploads.delete(key);
      this.updateAtcState();
    }).catch(err => {
      console.error('File upload failed:', key, err);
      this.uploadedFiles[key] = file.name;
      if (nameEl) nameEl.textContent = `${file.name} (upload failed)`;
      this.pendingUploads.delete(key);
      this.updateAtcState();
    });

    this.pendingUploads.set(key, uploadPromise);
    this.updateAtcState();
  }

  async uploadToShopify(file) {
    const UPLOAD_API = 'https://sixsis-upload-api-production.up.railway.app/upload';

    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(UPLOAD_API, {
      method: 'POST',
      body: formData
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || 'Upload failed');
    }

    const data = await response.json();
    if (data.success && data.url) {
      return data.url;
    }

    throw new Error('Upload failed - no URL returned');
  }

  updateAtcState() {
    if (!this.atcBtn) return;
    if (this.pendingUploads.size > 0) {
      this.atcBtn.textContent = `Uploading (${this.pendingUploads.size})...`;
      this.atcBtn.classList.add('is-loading');
    } else {
      this.atcBtn.textContent = this.atcBtn.getAttribute('data-label') || 'Add to Cart';
      this.atcBtn.classList.remove('is-loading');
    }
  }

  /* ── Add to Cart ── */
  bindAddToCart() {
    if (!this.atcBtn) return;

    this.atcBtn.addEventListener('click', async (e) => {
      e.preventDefault();
      this.clearMessages();

      // Collect items with qty > 0
      const items = this.collectItems();
      if (!items.length) {
        this.showError('Please enter a quantity for at least one size.');
        return;
      }

      // Check if below minimum — show popup confirmation
      const preCheckQty = items.reduce((s, i) => s + i.quantity, 0);
      if (this.minEnabled && preCheckQty > 0 && preCheckQty < this.minQty && this.setupFeeId && !this._minConfirmed) {
        this.showMinPopup(preCheckQty);
        return;
      }
      this._minConfirmed = false; // reset for next time

      // Add addon products for each checked location
      const addonItems = this.collectAddonItems(items);

      // Add setup fee if below minimum (count only main product items, not addons)
      const mainQty = items.reduce((s, i) => s + i.quantity, 0);
      if (this.minEnabled && mainQty > 0 && mainQty < this.minQty && this.setupFeeId) {
        items.push({
          id: parseInt(this.setupFeeId, 10),
          quantity: 1,
          properties: {
            '_Group': this.currentGroupId,
            '_For': this.productData?.title || 'Product',
            '_Protected': 'true'
          }
        });
      }

      // Now add addon items after the setup fee check
      items.push(...addonItems);

      // Wait for any pending uploads to finish
      if (this.pendingUploads.size > 0) {
        this.atcBtn.classList.add('is-loading');
        this.atcBtn.textContent = 'Waiting for uploads...';
        await Promise.all(this.pendingUploads.values());
      }

      // Re-collect items now that uploads are done (artwork URLs are ready)
      const finalItems = this.collectItems();
      const finalAddonItems = this.collectAddonItems(finalItems);
      items.length = 0;
      items.push(...finalItems, ...finalAddonItems);

      // Re-add setup fee if needed (count only main product items)
      const finalMainQty = finalItems.reduce((s, i) => s + i.quantity, 0);
      if (this.minEnabled && finalMainQty > 0 && finalMainQty < this.minQty && this.setupFeeId) {
        items.push({
          id: parseInt(this.setupFeeId, 10),
          quantity: 1,
          properties: {
            '_Group': this.currentGroupId,
            '_For': this.productData?.title || 'Product',
            '_Protected': 'true'
          }
        });
      }

      console.log('=== ATC DEBUG ===');
      console.log('Files:', JSON.stringify(this.uploadedFiles));
      console.log('Items:', JSON.stringify(items, null, 2));

      // Submit to Cart API
      this.atcBtn.classList.add('is-loading');
      this.atcBtn.textContent = 'Adding...';

      try {
        const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items })
        });

        const data = await response.json();

        if (response.ok) {
          this.showSuccess('Added to cart!');

          // Reset qty inputs
          this.querySelectorAll('[data-ca-qty]:not([disabled])').forEach(i => i.value = '0');
          this.updateTotal();

          // Refresh cart count in header
          this.refreshCartCount();

          // Redirect to cart after short delay
          setTimeout(() => { window.location.href = '/cart'; }, 800);
        } else {
          this.showError(data.description || data.message || 'Could not add to cart.');
        }
      } catch (err) {
        this.showError('Something went wrong. Please try again.');
        console.error('Custom Apparel ATC error:', err);
      } finally {
        this.atcBtn.classList.remove('is-loading');
        this.atcBtn.textContent = this.atcBtn.getAttribute('data-label') || 'Add to Cart';
      }
    });
  }

  collectItems() {
    const items = [];

    // Generate a unique group ID for this add-to-cart action
    this.currentGroupId = Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 6);

    // Get customization info for properties
    const serviceInfo = this.getServiceInfo();
    const artworkInfo = this.getArtworkInfo();
    const productName = this.productData?.title || '';

    const buildProps = () => {
      const properties = { '_Group': this.currentGroupId };
      if (serviceInfo.name) properties['_Service'] = serviceInfo.name;
      if (serviceInfo.location) properties['_Location'] = serviceInfo.location;
      Object.entries(artworkInfo).forEach(([key, val]) => {
        properties['Artwork - ' + key] = val;
      });
      return properties;
    };

    // 1. Collect from saved color slots
    this.colorSlots.forEach(slot => {
      Object.entries(slot.variants).forEach(([vid, qty]) => {
        if (qty <= 0) return;
        items.push({ id: parseInt(vid, 10), quantity: qty, properties: buildProps() });
      });
    });

    // 2. Collect from active grid (current color being edited)
    this.querySelectorAll('[data-ca-size-row]:not([style*="display: none"])').forEach(row => {
      const qtyInputs = row.querySelectorAll('[data-ca-qty]');
      const variantIds = row.querySelectorAll('[data-ca-variant-id]');

      qtyInputs.forEach((input, i) => {
        const qty = parseInt(input.value, 10) || 0;
        if (qty <= 0) return;

        const variantId = variantIds[i]?.value;
        if (!variantId) return;

        items.push({ id: parseInt(variantId, 10), quantity: qty, properties: buildProps() });
      });
    });

    return items;
  }

  getServiceInfo() {
    const checked = this.getCheckedAddons ? this.getCheckedAddons() : [];
    if (checked.length === 0) return {};
    const label = this.addonOptionsEl?.getAttribute('data-label') || 'Customization';
    const locations = checked.map(cb => cb.getAttribute('data-value') || '').filter(Boolean);
    return { name: label, location: locations.join(', ') };
  }

  getArtworkInfo() {
    const artwork = {};
    Object.entries(this.uploadedFiles).forEach(([key, val]) => {
      // val is either a URL string (uploaded) or a filename string (fallback)
      artwork[key] = val;
    });
    return artwork;
  }

  collectAddonItems(mainItems) {
    const checked = this.getCheckedAddons ? this.getCheckedAddons() : [];
    if (checked.length === 0) return [];

    const totalQty = mainItems.reduce((sum, item) => sum + item.quantity, 0);
    if (totalQty <= 0) return [];

    const productName = this.productData?.title || 'Product';
    const artworkInfo = this.getArtworkInfo();

    // Create one add-on line item per checked location
    return checked.map(cb => {
      const variantId = parseInt(cb.value, 10);
      const locationName = cb.getAttribute('data-value') || '';
      if (!variantId) return null;

      const properties = {
        '_Group': this.currentGroupId,
        '_For': productName,
        '_Location': locationName
      };

      // Attach artwork for this specific location (visible in cart)
      Object.entries(artworkInfo).forEach(([key, val]) => {
        if (key.toLowerCase().includes(locationName.toLowerCase()) || checked.length === 1) {
          properties['Artwork - ' + key] = val;
        }
      });

      return {
        id: variantId,
        quantity: totalQty,
        properties
      };
    }).filter(Boolean);
  }

  async refreshCartCount() {
    try {
      const res = await fetch(window.Shopify.routes.root + 'cart.js', { headers: { 'Accept': 'application/json' } });
      const cart = await res.json();
      // Update common cart count selectors
      document.querySelectorAll('[data-cart-count], .cart-count-bubble span, .cart-count').forEach(el => {
        el.textContent = cart.item_count;
      });
    } catch (e) { /* silent */ }
  }

  /* ── Mobile Size Grid ── */
  initMobileSizeGrid() {
    if (window.innerWidth > 749) return;

    // Build a stacked card layout from the table data
    this.querySelectorAll('.ca-size-grid').forEach(grid => {
      const table = grid.querySelector('.ca-size-grid__table');
      if (!table) return;

      const mobileContainer = document.createElement('div');
      mobileContainer.className = 'ca-size-grid__mobile';

      // Get all size rows (visible and hidden)
      grid.querySelectorAll('[data-ca-size-row]').forEach(row => {
        const rowColor = row.getAttribute('data-ca-size-row');
        const isVisible = row.style.display !== 'none';

        const qtyInputs = row.querySelectorAll('[data-ca-qty]');
        const variantIds = row.querySelectorAll('[data-ca-variant-id]');

        // Get size labels from the table header
        const headers = table.querySelectorAll('thead th');

        qtyInputs.forEach((input, i) => {
          const sizeName = headers[i + 1]?.textContent?.trim() || `Size ${i + 1}`;
          const variantInput = variantIds[i];
          const maxQty = input.getAttribute('max');
          const inventory = maxQty || '0';
          const isSoldOut = !input.closest('td') || input.closest('td').querySelector('.ca-size-grid__soldout');

          const card = document.createElement('div');
          card.className = 'ca-size-card';
          card.setAttribute('data-ca-mobile-row', rowColor);
          if (!isVisible) card.style.display = 'none';

          if (isSoldOut && !input.parentNode) {
            card.innerHTML = `
              <div class="ca-size-card__info">
                <span class="ca-size-card__name">${sizeName}</span>
              </div>
              <span class="ca-size-card__soldout">Sold out</span>
            `;
          } else {
            card.innerHTML = `
              <div class="ca-size-card__info">
                <span class="ca-size-card__name">${sizeName}</span>
                <span class="ca-size-card__stock">${inventory} in stock</span>
              </div>
            `;
            // Move the actual input into the card so it's the same DOM element
            card.appendChild(input.cloneNode(true));
            // Sync the cloned input with the original
            const cloned = card.querySelector('[data-ca-qty]');
            cloned.addEventListener('input', () => {
              input.value = cloned.value;
              input.dispatchEvent(new Event('input', { bubbles: true }));
            });
            cloned.addEventListener('change', () => {
              input.value = cloned.value;
              input.dispatchEvent(new Event('change', { bubbles: true }));
            });
            // Sync original → clone when color changes
            const observer = new MutationObserver(() => {
              cloned.value = input.value;
              cloned.disabled = input.disabled;
            });
            observer.observe(input, { attributes: true, attributeFilter: ['disabled', 'value'] });
          }

          mobileContainer.appendChild(card);
        });
      });

      grid.appendChild(mobileContainer);
    });

    // Override selectColor to also toggle mobile cards
    const origSelectColor = this.selectColor.bind(this);
    this.selectColor = (color, swatch) => {
      origSelectColor(color, swatch);
      this.querySelectorAll('[data-ca-mobile-row]').forEach(card => {
        card.style.display = card.getAttribute('data-ca-mobile-row') === color ? '' : 'none';
      });
    };
  }

  /* ── Multi-Color Slot System ── */
  initMultiColor() {
    this.addColorBtn = this.querySelector('[data-ca-add-color]');
    this.slotsContainer = this.querySelector('[data-ca-slots]');
    this.sizeStep = this.querySelector('[data-ca-size-step]');
    if (!this.addColorBtn || !this.slotsContainer) return;

    // Show button when any qty > 0
    this.querySelectorAll('[data-ca-qty]').forEach(input => {
      input.addEventListener('input', () => this.toggleAddColorBtn());
    });

    this.addColorBtn.addEventListener('click', () => this.saveCurrentSlot());
  }

  toggleAddColorBtn() {
    if (!this.addColorBtn) return;
    const hasQty = Array.from(this.querySelectorAll('[data-ca-qty]:not([disabled])')).some(i => parseInt(i.value, 10) > 0);
    this.addColorBtn.hidden = !hasQty;
  }

  getCurrentColor() {
    const active = this.querySelector('.ca-color-swatch.is-active');
    return active ? active.getAttribute('data-ca-color') : '';
  }

  getCurrentSwatchStyle() {
    const active = this.querySelector('.ca-color-swatch.is-active');
    return active ? active.getAttribute('style') : '';
  }

  getActiveGridVariants() {
    const variants = {};
    this.querySelectorAll('[data-ca-size-row]:not([style*="display: none"])').forEach(row => {
      const qtyInputs = row.querySelectorAll('[data-ca-qty]');
      const variantIds = row.querySelectorAll('[data-ca-variant-id]');
      qtyInputs.forEach((input, i) => {
        const qty = parseInt(input.value, 10) || 0;
        const vid = variantIds[i]?.value;
        if (qty > 0 && vid) variants[vid] = qty;
      });
    });
    return variants;
  }

  saveCurrentSlot() {
    const color = this.getCurrentColor();
    if (!color) return;

    const variants = this.getActiveGridVariants();
    const totalQty = Object.values(variants).reduce((s, q) => s + q, 0);
    if (totalQty <= 0) return;

    // Check if editing an existing slot for this color
    const existingIdx = this.colorSlots.findIndex(s => s.color === color);
    const slot = { color, swatchStyle: this.getCurrentSwatchStyle(), variants };

    if (existingIdx >= 0) {
      this.colorSlots[existingIdx] = slot;
    } else {
      this.colorSlots.push(slot);
    }

    // Reset the grid
    this.querySelectorAll('[data-ca-qty]:not([disabled])').forEach(i => { i.value = '0'; });
    this.addColorBtn.hidden = true;

    this.renderSlotSummaries();
    this.updateTotal();
  }

  removeSlot(index) {
    this.colorSlots.splice(index, 1);
    this.renderSlotSummaries();
    this.updateTotal();
  }

  editSlot(index) {
    const slot = this.colorSlots[index];
    if (!slot) return;

    // Save current grid first if it has qty
    const currentVariants = this.getActiveGridVariants();
    const currentQty = Object.values(currentVariants).reduce((s, q) => s + q, 0);
    const currentColor = this.getCurrentColor();
    if (currentQty > 0 && currentColor && currentColor !== slot.color) {
      // Auto-save current color before switching
      const existingIdx = this.colorSlots.findIndex(s => s.color === currentColor);
      const currentSlot = { color: currentColor, swatchStyle: this.getCurrentSwatchStyle(), variants: currentVariants };
      if (existingIdx >= 0) {
        this.colorSlots[existingIdx] = currentSlot;
      } else {
        this.colorSlots.push(currentSlot);
      }
    }

    // Remove slot from array (it's being edited in the grid now)
    this.colorSlots.splice(index, 1);

    // Click the color swatch to switch to this color
    const swatch = Array.from(this.colorSwatches).find(s => s.getAttribute('data-ca-color') === slot.color);
    if (swatch) this.selectColor(slot.color, swatch);

    // Fill in the saved quantities
    setTimeout(() => {
      this.querySelectorAll('[data-ca-variant-id]').forEach(hidden => {
        const vid = hidden.value;
        if (slot.variants[vid]) {
          const td = hidden.closest('td');
          const qtyInput = td?.querySelector('[data-ca-qty]');
          if (qtyInput && !qtyInput.disabled) {
            qtyInput.value = slot.variants[vid];
          }
        }
      });
      this.updateTotal();
      this.toggleAddColorBtn();
      this.renderSlotSummaries();
    }, 100);
  }

  renderSlotSummaries() {
    if (!this.slotsContainer) return;

    if (this.colorSlots.length === 0) {
      this.slotsContainer.hidden = true;
      this.slotsContainer.innerHTML = '';
      return;
    }

    this.slotsContainer.hidden = false;
    this.slotsContainer.innerHTML = this.colorSlots.map((slot, i) => {
      const totalQty = Object.values(slot.variants).reduce((s, q) => s + q, 0);
      return `
        <div class="ca-slot" data-slot-index="${i}">
          <span class="ca-slot__swatch" style="${slot.swatchStyle}"></span>
          <div class="ca-slot__info">
            <span class="ca-slot__color">${slot.color}</span>
            <span class="ca-slot__qty">${totalQty} piece${totalQty !== 1 ? 's' : ''}</span>
          </div>
          <div class="ca-slot__actions">
            <button type="button" class="ca-slot__btn ca-slot__btn--edit" data-slot-edit="${i}" title="Edit">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
            </button>
            <button type="button" class="ca-slot__btn ca-slot__btn--remove" data-slot-remove="${i}" title="Remove">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
            </button>
          </div>
        </div>
      `;
    }).join('');

    // Bind edit/remove
    this.slotsContainer.querySelectorAll('[data-slot-edit]').forEach(btn => {
      btn.onclick = () => this.editSlot(parseInt(btn.getAttribute('data-slot-edit'), 10));
    });
    this.slotsContainer.querySelectorAll('[data-slot-remove]').forEach(btn => {
      btn.onclick = () => this.removeSlot(parseInt(btn.getAttribute('data-slot-remove'), 10));
    });
  }

  getSlotsTotalQty() {
    return this.colorSlots.reduce((sum, slot) => {
      return sum + Object.values(slot.variants).reduce((s, q) => s + q, 0);
    }, 0);
  }

  /* ── Restore Edit State ── */
  restoreEditState() {
    if (!window.location.search.includes('edit=1')) return;

    let state;
    try {
      const raw = sessionStorage.getItem('sixsis_edit_order');
      if (!raw) return;
      state = JSON.parse(raw);
      sessionStorage.removeItem('sixsis_edit_order');
    } catch(e) { return; }

    console.log('[EditRestore] Restoring state:', state);

    // 1. Select color
    if (state.color) {
      const swatch = Array.from(this.colorSwatches).find(s => {
        const c = (s.getAttribute('data-ca-color') || '').trim();
        return c === state.color || c.toLowerCase() === state.color.toLowerCase();
      });
      if (swatch) {
        setTimeout(() => this.selectColor(state.color, swatch), 100);
      }
    }

    // 2. Fill in size quantities
    if (state.variants && Object.keys(state.variants).length > 0) {
      setTimeout(() => {
        this.querySelectorAll('[data-ca-variant-id]').forEach(hidden => {
          const vid = hidden.value;
          if (state.variants[vid]) {
            const td = hidden.closest('td');
            const qtyInput = td?.querySelector('[data-ca-qty]');
            if (qtyInput && !qtyInput.disabled) {
              qtyInput.value = state.variants[vid];
              qtyInput.dispatchEvent(new Event('input', { bubbles: true }));
            }
          }
        });
        // Also sync mobile cards if they exist
        this.querySelectorAll('.ca-size-card [data-ca-qty]').forEach(cloned => {
          // Find matching original by traversing
          const card = cloned.closest('.ca-size-card');
          if (!card || card.style.display === 'none') return;
        });
        this.updateTotal();
      }, 300);
    }

    // 3. Check customization addons
    if (state.addons && state.addons.length > 0) {
      setTimeout(() => {
        this.addonCheckboxes.forEach(cb => {
          if (state.addons.includes(cb.value)) {
            cb.checked = true;
            cb.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
        this.updateUploadZones();
        this.updateAddonPrice();
        this.updateTotal();
      }, 400);
    }

    // 4. Restore artwork URLs (show as already uploaded)
    if (state.artwork && Object.keys(state.artwork).length > 0) {
      setTimeout(() => {
        Object.entries(state.artwork).forEach(([location, url]) => {
          this.uploadedFiles[location] = url;
          // Find the upload zone for this location and show the preview
          this.uploadZones.forEach(zone => {
            const zoneName = (zone.getAttribute('data-ca-upload-zone') || '').toLowerCase().trim();
            if (zoneName === location.toLowerCase().trim() || (zoneName === 'generic' && Object.keys(state.artwork).length === 1)) {
              const preview = zone.querySelector('.ca-uploads__preview');
              const nameEl = zone.querySelector('.ca-uploads__preview-name');
              const thumb = zone.querySelector('.ca-uploads__preview-thumb');
              const dropzone = zone.querySelector('.ca-uploads__dropzone');
              const filename = url.split('/').pop()?.split('?')[0] || 'artwork';

              if (preview) preview.classList.add('has-file');
              if (nameEl) nameEl.textContent = `✓ ${filename} (from previous order)`;
              if (dropzone) dropzone.style.display = 'none';
              // Show thumbnail if it's an image URL
              if (thumb && (url.includes('.png') || url.includes('.jpg') || url.includes('.jpeg') || url.includes('.webp'))) {
                thumb.src = url.replace('dl=0', 'raw=1');
                thumb.style.display = '';
              }
            }
          });
        });
      }, 600);
    }

    // Edit restore is silent — no visible banner
  }

  /* ── Minimum Threshold Popup ── */
  showMinPopup(currentQty) {
    const popup = this.querySelector('[data-ca-min-popup]');
    if (!popup) {
      // No popup HTML — just proceed with ATC
      this._minConfirmed = true;
      this.atcBtn.click();
      return;
    }

    const msgEl = popup.querySelector('[data-ca-min-popup-msg]');
    const needed = this.minQty - currentQty;
    const feeFormatted = this.formatMoney(this.setupFeePrice);
    if (msgEl) {
      msgEl.innerHTML = `You have <strong>${currentQty} pieces</strong> in your order. The minimum for <strong>free setup</strong> is <strong>${this.minQty} pieces</strong>.<br><br>A <strong>${feeFormatted} setup fee</strong> will be added. Add <strong>${needed} more</strong> to waive it.`;
    }

    popup.removeAttribute('hidden');
    document.body.style.overflow = 'hidden';

    // Close handlers
    const close = () => {
      popup.setAttribute('hidden', '');
      document.body.style.overflow = '';
    };

    popup.querySelectorAll('[data-ca-min-popup-close]').forEach(el => {
      el.onclick = close;
    });

    // Confirm — proceed with ATC including setup fee
    const confirmBtn = popup.querySelector('[data-ca-min-popup-confirm]');
    if (confirmBtn) {
      confirmBtn.onclick = () => {
        close();
        this._minConfirmed = true;
        this.atcBtn.click();
      };
    }
  }

  /* ── Messages ── */
  showError(msg) {
    if (this.errorEl) {
      this.errorEl.textContent = msg;
      this.errorEl.classList.add('is-visible');
    }
  }

  showSuccess(msg) {
    if (this.successEl) {
      this.successEl.textContent = msg;
      this.successEl.classList.add('is-visible');
    }
  }

  clearMessages() {
    if (this.errorEl) { this.errorEl.textContent = ''; this.errorEl.classList.remove('is-visible'); }
    if (this.successEl) { this.successEl.textContent = ''; this.successEl.classList.remove('is-visible'); }
  }
}

if (!customElements.get('custom-apparel-form')) {
  customElements.define('custom-apparel-form', CustomApparelForm);
}
